import { z } from 'zod';

/**
 * Ассеты и медиатека (§24, §47).
 *
 * Загрузка идёт напрямую в хранилище по подписанной ссылке: основное видео
 * персоны — это 1-3 минуты с телефона, и гонять такое через API нельзя.
 */

export const AssetKind = {
  VIDEO: 'video',
  IMAGE: 'image',
  AUDIO: 'audio',
  OVERLAY: 'overlay',
  DOCUMENT: 'document',
  LOGO: 'logo',
  FONT: 'font',
} as const;
export type AssetKind = (typeof AssetKind)[keyof typeof AssetKind];

export const assetKindLabels: Record<AssetKind, string> = {
  video: 'Видео',
  image: 'Изображения',
  audio: 'Аудио',
  overlay: 'Графика',
  document: 'Документы',
  logo: 'Логотипы',
  font: 'Шрифты',
};

export const AssetSource = {
  USER_UPLOAD: 'user_upload',
  PERSONA_CAPTURE: 'persona_capture',
  AUTO_EXTRACTED: 'auto_extracted',
  GENERATED: 'generated',
  MOTION_RENDER: 'motion_render',
  COMPOSITION: 'composition',
} as const;
export type AssetSource = (typeof AssetSource)[keyof typeof AssetSource];

/**
 * Что вообще принимаем на загрузку от пользователя.
 * Список закрытый: всё, чего тут нет, отклоняется до выдачи ссылки.
 */
export const allowedMimeTypes: Record<AssetKind, string[]> = {
  video: ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-matroska'],
  image: ['image/jpeg', 'image/png', 'image/webp', 'image/avif', 'image/heic', 'image/heif'],
  audio: ['audio/mpeg', 'audio/mp4', 'audio/wav', 'audio/webm', 'audio/aac', 'audio/ogg'],
  overlay: ['video/webm', 'video/quicktime', 'image/png'],
  document: ['application/pdf', 'text/plain', 'text/markdown'],
  logo: ['image/png', 'image/svg+xml', 'image/webp'],
  font: ['font/woff2', 'font/woff', 'font/ttf', 'font/otf'],
};

/** Что пользователь может загрузить сам (§24). Остальное создаёт система. */
export const uploadableKinds: AssetKind[] = ['video', 'image', 'audio', 'document', 'logo', 'font'];

export const uploadIntentSchema = z
  .object({
    kind: z.enum(uploadableKinds as [AssetKind, ...AssetKind[]]),
    mime: z.string().min(3).max(120),
    bytes: z.number().int().positive(),
    /** sha256 в hex. Позволяет не хранить один и тот же файл дважды. */
    checksum: z.string().regex(/^[a-f0-9]{64}$/i, 'Ожидается sha256 в hex'),
    originalName: z.string().trim().max(255).optional(),
  })
  .strict();
export type UploadIntentInput = z.infer<typeof uploadIntentSchema>;

export interface UploadIntent {
  assetId: string;
  /** null, если такой файл у пользователя уже есть — загружать заново не нужно. */
  uploadUrl: string | null;
  /** Заголовки, с которыми подписана ссылка. Отправлять ровно их. */
  headers: Record<string, string>;
  expiresAt: string | null;
  duplicate: boolean;
}

export interface AssetView {
  id: string;
  kind: AssetKind;
  source: AssetSource;
  status: 'pending' | 'ready' | 'failed';
  mime: string;
  bytes: number;
  width: number | null;
  height: number | null;
  durationSec: number | null;
  originalName: string | null;
  createdAt: string;
  /** Подписанные ссылки с коротким TTL. Публичного доступа к бакету нет. */
  url: string | null;
  posterUrl: string | null;
}

export const mediaQuerySchema = z
  .object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(60).default(24),
    kind: z.enum(Object.values(AssetKind) as [AssetKind, ...AssetKind[]]).optional(),
  })
  .strict();
export type MediaQuery = z.infer<typeof mediaQuerySchema>;

export interface StorageUsage {
  usedBytes: number;
  limitBytes: number;
  assetCount: number;
}
